import React, { useState } from 'react';
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  Divider,
  Button,
  Box,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNotifications } from './NotificationContext';
import { getFormattedDate } from '@utilities/common';
import './notifications.scss';

interface NotificationBellProps {
  restaurantId?: string;
}

export default function NotificationBell({ restaurantId }: NotificationBellProps) {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    openNotification,
    watchRestaurant,
  } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  React.useEffect(() => {
    if (restaurantId) {
      watchRestaurant(restaurantId);
    }
  }, [restaurantId, watchRestaurant]);

  const relevantNotifications = restaurantId
    ? notifications.filter((n) => n.restaurantId === restaurantId)
    : notifications;

  const relevantUnread = relevantNotifications.filter((n) => !n.read).length;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="Notifications"
        onClick={handleOpen}
        className="notification-bell"
        size="large"
      >
        <Badge badgeContent={relevantUnread || unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        className="notification-menu"
        PaperProps={{ sx: { width: 320, maxHeight: 400 } }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2">Notifications</Typography>
          {relevantUnread > 0 && (
            <Button size="small" onClick={markAllAsRead}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />
        {relevantNotifications.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No notifications yet" />
          </MenuItem>
        )}
        {relevantNotifications.slice(0, 20).map((notification) => (
          <MenuItem
            key={notification.id}
            onClick={() => {
              openNotification(notification);
              markAsRead(notification.id);
              handleClose();
            }}
            sx={{ opacity: notification.read ? 0.7 : 1, whiteSpace: 'normal' }}
          >
            <ListItemText
              primary={notification.title}
              secondary={
                <>
                  {notification.body}
                  <br />
                  <Typography component="span" variant="caption" color="text.secondary">
                    {getFormattedDate(new Date(notification.createdAt))}
                  </Typography>
                </>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
